/* TourSlots — Rondleiding-slots beheer voor de publieke aanmeldflow.
 * Toont een dialog waar admin nieuwe tijdsloten toevoegt en bestaande verwijdert.
 * Extracted from AanmeldingenPage.jsx (iter72 refactor).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DateField from "@/components/DateField";
import { Calendar, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function TourSlots() {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    location_id: "", datum: "", tijd_van: "10:00",
    tijd_tot: "11:00", capaciteit: 4, notitie: "",
  });

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/rondleiding-slots"),
        api.get("/locations"),
      ]);
      setSlots(s.data || []);
      setLocations(l.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!form.location_id || !form.datum || !form.tijd_van || !form.tijd_tot) {
      toast.error("Vul alle verplichte velden in"); return;
    }
    try {
      await api.post("/rondleiding-slots", form);
      toast.success("Slot toegevoegd");
      setForm({ ...form, datum: "", notitie: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Slot verwijderen?")) return;
    try {
      await api.delete(`/rondleiding-slots/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="rondleiding-slots-btn">
        <Calendar className="h-4 w-4 mr-1" /> Rondleiding-slots
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="slots-dialog">
          <DialogHeader>
            <DialogTitle>Rondleiding-slots</DialogTitle>
            <DialogDescription>Beheer welke rondleidings-momenten ouders kunnen kiezen in het publieke aanmeldformulier.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-6 gap-2 p-3 rounded-md bg-neutral-50">
            <div className="col-span-2"><Label className="text-xs">Locatie *</Label>
              <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="slot-locatie" className="h-8"><SelectValue placeholder="Kies" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Datum *</Label>
              <DateField value={form.datum} onChange={(v) => setForm({ ...form, datum: v })} data-testid="slot-datum" />
            </div>
            <div><Label className="text-xs">Van *</Label>
              <Input type="time" className="h-8" value={form.tijd_van} onChange={e => setForm({ ...form, tijd_van: e.target.value })} />
            </div>
            <div><Label className="text-xs">Tot *</Label>
              <Input type="time" className="h-8" value={form.tijd_tot} onChange={e => setForm({ ...form, tijd_tot: e.target.value })} />
            </div>
            <div><Label className="text-xs">Cap.</Label>
              <Input type="number" min="1" className="h-8" value={form.capaciteit} onChange={e => setForm({ ...form, capaciteit: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="col-span-5"><Label className="text-xs">Notitie</Label>
              <Input className="h-8" value={form.notitie} onChange={e => setForm({ ...form, notitie: e.target.value })} placeholder="Optioneel — bijv. 'Groep max 3 gezinnen'" />
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={add} style={{ background: "var(--sage)" }} className="text-white h-8 w-full" data-testid="slot-add-btn">
                <Plus className="h-3 w-3 mr-1" /> Voeg toe
              </Button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
            {slots.length === 0 && <div className="text-xs text-neutral-500 text-center py-6">Nog geen slots aangemaakt.</div>}
            {slots.map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 border rounded text-xs" data-testid={`slot-row-${s.id}`}>
                <div>
                  <span className="font-medium">{new Date(s.datum).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                  {" · "}{s.tijd_van}–{s.tijd_tot}
                  <span className="ml-2 text-neutral-500">{s.location_name}</span>
                  {s.notitie && <span className="ml-2 italic text-neutral-500">— {s.notitie}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${(s.geboekt || 0) >= s.capaciteit ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                    {s.geboekt || 0}/{s.capaciteit}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`slot-del-${s.id}`}
                    className="text-red-600 hover:text-red-700 h-6 px-1">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
